import { observable, action, computed, autorun, runInAction } from 'mobx'

import uuid                                      from 'uuid'
import _                                         from 'lodash'
import xhr                                       from 'helpers/XHR'
import { postEvent }                             from 'helpers/Segment'

import { SIDEBAR }                               from 'stores/UiStore'
import uiStore                                   from 'stores/UiStore'
import SMSInboxStore                             from 'stores/SMSInboxStore'
import getError                                  from 'helpers/ErrorParser'
import { setter }                                from 'mobx-decorators'
import Conversation                              from 'stores/models/Conversation'
import Scroller                                  from 'stores/models/Scroller'
import commsStore                                from 'stores/CommsStore'

export class SMSConversationStore {
  @observable limit = 20
  @setter @observable isLoading = false
  @observable conversations = observable.map()
  @observable scrollers = observable.map()
  @setter @observable isError = null

  constructor() {
    this.initAutoruns()
  }

  // Computed Values

  @computed
  get descMessages() {
    const conversation = this.getCurrentConversation()

    return conversation
      ? _.sortBy(conversation.messages.values(), (m) => m.createdAt)
      : []
  }

  @computed
  get shouldLoadMore() {
    const scroller = this.getCurrentScroller()

    if (this.isLoading || !scroller || scroller.isFilled) return false
    return true
  }

  // Actions

  @action initAutoruns = () => {
    this.autoErrorNotifier()
  }

  @action autoErrorNotifier = () => {
    this.autoErrorDisposer = autorun('Watch errors', () => {
      if (this.isError && !this.isError.hideNotification) {
        uiStore.addNotification({
          title:   this.isError.title,
          message: this.isError.message,
          type:    this.isError.type || 'error'
        })
      }
    })
  }

  @action
  addMessage = (msg) => {
    const conversation = this.conversations.get(msg.conversation_id)

    conversation && conversation.add(msg)
  }

  @action
  removeMessage = (msg) => {
    const conversation = this.conversations.get(msg.conversationId)

    conversation && conversation.remove(msg)
  }

  @action
  fetchConversation = async(id) => {
    const scroller = this.getCurrentScroller()
    const limit = scroller ? scroller.limit : this.limit

    this.isLoading = true
    try {
      const res = await xhr.get(`/commo/sms/conversation/${id}`, {
        params: {
          only: [
            'id',
            'created_at',
            'conversation_id',
            'broadcast_id',
            'direction',
            'sent_state',
            'body',
            'media_url',
            'media_type',
            'read_status',
            'contact.id',
            'contact.name',
            'contact.phone',
            'contact.relationship',
            'contact.preferred_language',
            'language',
            'meta',
            'explicit_content'
          ].join(','),
          limit: limit,
          page:  1
        }
      })

      this.fetchConversationOK(id, res.headers, res.data)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.isLoading = false
    }
  }

  @action
  fetchConversationOK = (id, headers, data) => {
    this.updateConversation(id, data)
    this.updateScroller(id, headers)
  }

  getConversationParams = (id, data) => ({
    id,
    store:    this,
    messages: data
  })

  @action
  updateConversation = (id, data) => {
    const params = this.getConversationParams(id, data)
    const conversation = this.conversations.get(id)

    conversation
      ? conversation.updateMessages(data)
      : this.conversations.set(id, new Conversation(params))
  }

  @action
  initiateConversation = async(contact) => {
    try {
      if(this.isLoading == false){
        this.setIsLoading(true)
        uiStore.setCurrentContact(contact)
        uiStore.setSelectedSidebar(SIDEBAR.SMS)
        uiStore.setShowInbox(false)
        uiStore.setSidebarMaxHeight(true)

        const { data } = await xhr.post('/commo/conversations', {
          number:     contact.phone,
          contact_id: contact.id
        })

        this.initiateConversationOk(contact, data)
      }
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsLoading(false)
    }
  }

  @action
  initiateConversationOk = (contact, data) => {
    uiStore.setCurrentContact(contact)
    uiStore.setCurrentConversation(data.id)
    uiStore.setSelectedSidebar(null)
    uiStore.setSelectedSidebar(SIDEBAR.SMS)
    uiStore.setShowInbox(false)
    uiStore.setSidebarMaxHeight(true)
    uiStore.setIsCurrentSMSConversationActive(data.active)
  }

  @action
  sendMessage = (msg, id, attachment = null, isAutoTranslate = false) => {
    postEvent('web:sms:send', {
      contact_id: id,
      attachment: attachment.length ? true : false
    })

    if (attachment.length) {
      this.sendWithAttachment(msg, id, attachment, isAutoTranslate)
    } else {
      this.sendWithoutAttachment(msg, id, isAutoTranslate)
    }
  }

  @action
  sendMessageOK = ({ data }) => {
    this.addMessage(data)
    SMSInboxStore.addInboxItem(data)
    uiStore.setShouldScrollToBottom(true)
  }

  getAttachmentData = (msg, id, attachment, isAutoTranslate) => {
    const data = new FormData()

    data.append('contact_id', id)
    data.append('body', msg)
    attachment.forEach((a) => data.append('attachment[]', a))
    data.append('auto_translate', isAutoTranslate)

    return data
  }

  @action
  removeAttachment = async (id) => {
    try {
      const res = await xhr.delete(`/commo/sms/remove_attachment/${id}`)
      
      // Accept 200, 204, or even 500 if the attachment was actually deleted
      if (res.status === 200 || res.status === 204 || res.status === 500) {
        // Update message in SMS conversation view (if present)
        const conversation = this.conversations.get(uiStore.currentConversation)
        if (conversation) {
          const message = conversation.messages.get(id)
          if (message) {
            // Clear the attachment using the action (mediaUrl/mediaType are now observable)
            message.clearAttachment()
          }
        }
        
        // Update communication in CommsStore (for CommsInfo view)
        const communication = _.find(commsStore.communications, { id })
        if (communication) {
          runInAction(() => {
            communication.mediaUrl = null
            communication.explicitContent = null
          })
        }
        
        uiStore.addNotification({
          title:   'Success',
          message: 'Attachment removed successfully',
          type:    'success'
        })
        return
      }

      throw new Error('Failed to remove attachment.')
    } catch (e) {
      console.error('Remove attachment error:', e)
      // Even if there's an error, try to update the UI
      const conversation = this.conversations.get(uiStore.currentConversation)
      if (conversation) {
        const message = conversation.messages.get(id)
        if (message) {
          message.clearAttachment()
        }
      }

      const communication = _.find(commsStore.communications, { id })
      if (communication) {
        runInAction(() => {
          communication.mediaUrl = null
          communication.explicitContent = null
        })
      }

      this.setIsError(getError(e))
    }
  }

  @action
  createVideoRoom = async(contactId, roomId) => {
    try {
      const res = await xhr.post('/commo/video/init', {
        contact_id: contactId,
        room_id:    roomId
      })

      if (res.status === 201) {
        this.createVideoRoomOK(res)
        return
      }
    } catch (e) {
      console.log('Error creating video room')
    }
  }

  @action
  createVideoRoomOK = () => {
    console.log('Video Room created')
    uiStore.setShouldScrollToBottom(true)
  }

  @action
  sendWithAttachment = async(
    msg,
    id,
    attachment,
    isAutoTranslate
  ) => {
    const data = this.getAttachmentData(
      msg,
      id,
      attachment,
      isAutoTranslate
    )

    try {
      const res = await xhr.post(
        '/commo/sms/send_message/contact',
        data,
        {
          'Content-Type': 'multipart/form-data'
        }
      )

      if (res.status === 201) {
        this.sendMessageOK(res)
        return
      }

      throw new Error('Failed with attachment.')
    } catch (e) {
      this.handleFailedMessage(msg, attachment)
      this.setIsError(getError(e))
    }
  }

  @action
  sendWithoutAttachment = async(msg, id, isAutoTranslate) => {
    try {
      const res = await xhr.post('/commo/sms/send_message/contact', {
        contact_id:     id,
        body:           msg,
        auto_translate: isAutoTranslate
      })

      if (res.status === 201) {
        this.sendMessageOK(res)
        return
      }

      throw new Error('Failed without attachment.')
    } catch (e) {
      this.handleFailedMessage(msg)
      this.setIsError(getError(e))
    }
  }

  @action
  handleFailedMessage = (msg, attachment = null) => {
    const _id = uuid()
    let mediaUrl = null

    if (attachment && attachment instanceof File) {
      mediaUrl = window.URL.createObjectURL(attachment)
    }

    this.addMessage({
      id:              _id,
      direction:       'outbound',
      conversation_id: uiStore.currentConversation,
      body:            msg,
      status:          'failed',
      contact:         uiStore.currentContact.id,
      media_url:       mediaUrl,
      attachment:      attachment
    })
  }

  @action
  retryMessage(msg) {
    if (msg.attachment) {
      this.retrySendMessageWithAttachment(msg)
    } else {
      this.retrySendMessage(msg)
    }
  }

  @action
  retrySendMessage = async(msg) => {
    try {
      msg.setStatus('retrying')
      const res = await xhr.post('/commo/sms/send_message/contact', {
        contact_id: msg.contact,
        body:       msg.body
      })

      if (res.status === 201) {
        this.removeMessage(msg)
        this.sendMessageOK(res)
        return
      }

      throw new Error('Retry failed.')
    } catch (e) {
      msg.setStatus('failed')
      this.setIsError(getError(e))
    }
  }

  @action
  retrySendMessageWithAttachment = async(msg) => {
    try {
      const { contact, body, attachment } = msg
      const data = this.getAttachmentData(body, contact, attachment)

      msg.setStatus('retrying')
      const res = await xhr.post(
        '/commo/sms/send_message/contact',
        data,
        {
          'Content-Type': 'multipart/form-data'
        }
      )

      if (res.status === 201) {
        this.removeMessage(msg)
        this.sendMessageOK(res)
        return
      }

      throw new Error('Retry failed.')
    } catch (e) {
      msg.setStatus('failed')
      this.setIsError(getError(e))
    }
  }

  @action
  setRead = async(id) => {
    try {
      await xhr.put(`/commo/sms_log/${id}/read`)
    } catch (e) {
      this.setIsError(getError(e))
    }
  }

  @action
  setAllAsRead = async(conversationID) => {
    try {
      // Get the unread count BEFORE making the API call
      const conversation = SMSInboxStore.inbox.get(conversationID)
      const unreadCount = conversation && conversation.total_unread ? conversation.total_unread : 0

      await xhr.post(`/commo/sms/conversation/${conversationID}/read`)

      // Update conversation's unread count
      if (conversation) {
        conversation.total_unread = 0
      }

      // Decrement global unread count immediately for real-time badge update
      if (unreadCount > 0) {
        const newTotal = Math.max(0, (SMSInboxStore.totalUnread || 0) - unreadCount)
        SMSInboxStore.setTotalUnread(newTotal)
      }
    } catch (e) {
      this.setIsError(getError(e))
    }
  }

  @action
  delete = (id) => {
    this.conversations.delete(id)
    this.scrollers.delete(id)
  }

  getCurrentConversation = () => {
    return this.conversations.get(uiStore.currentConversation)
  }

  loadMore = () => {
    if (!this.shouldLoadMore) return

    // retrieve scroller for the current conversation
    const scroller = this.getCurrentScroller()

    // increase limit to fetch more data
    scroller.increment()

    // fetch more data
    this.fetchConversation(uiStore.currentConversation)

    uiStore.setShouldScrollToBottom(false)
  }

  getCurrentScroller = () => {
    return this.scrollers.get(uiStore.currentConversation)
  }

  updateScroller = (id, headers) => {
    const scroller = this.scrollers.get(id)
    const total = parseInt(headers.total)

    if (scroller) {
      scroller.setTotal(total)
    } else {
      this.scrollers.set(id, new Scroller(this.limit, total))
    }
  }
}

export default new SMSConversationStore()
